import * as THREE from 'three';
import { $selectedObjects, deselectAll } from '../stores/selectionStore';
import { ActiveSelection } from './activeSelection';
import { SceneCameraController } from './camera/sceneCameraController';
import { Floor } from './floor';
import { SceneKeybindHandler } from './input/sceneKeybindHandler';
import { ScenePlaceInputHandler } from './input/scenePlaceInputHandler';
import {
  type ScenePointerContext,
  type ScenePointerInputHandler,
} from './input/scenePointerInputHandler';
import { SceneSelectionInputHandler } from './input/sceneSelectionInputHandler';
import type { SceneObject } from './sceneObject';
import { SceneObjectCollection } from './sceneObjectCollection';
import { SelectableObject } from './selectableObject';
import { MarqueeViewportSelector } from './selection/marqueeViewportSelector';
import { PlaceTool } from './tools/placeTool';
import { SelectMarqueeTool } from './tools/selectMarqueeTool';
import { $cameraAngleToOrigin } from '@/stores/settingsStore';
import {
  $activeTool,
  type EditorTool,
  getActiveToolConfig,
} from '@/stores/toolStore';

const options = {
  floorSize: 8 * 15,
  floorTextureRepeat: 15,
  background: '#0f172a',
  fog: {
    near: 50,
    far: 60,
  },
  camera: {
    fov: 50,
    aspect: 1,
    near: 0.1,
    far: 200,
    distance: 35,
  },
};

export class BasicScene {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly cameraController: SceneCameraController;
  readonly renderer: THREE.WebGLRenderer;
  readonly observer: ResizeObserver;
  readonly sceneObjects: SceneObjectCollection;
  readonly activeSelection: ActiveSelection;
  readonly marqueeViewportSelector = new MarqueeViewportSelector();
  readonly placeTool = new PlaceTool();
  readonly selectMarqueeTool = new SelectMarqueeTool();
  readonly placeInputHandler = new ScenePlaceInputHandler(this.placeTool);
  selectionInputHandler!: SceneSelectionInputHandler;
  readonly pointerInputHandlers: ScenePointerInputHandler[] = [];
  readonly keybindHandler = new SceneKeybindHandler();
  readonly raycaster = new THREE.Raycaster();
  readonly pointer = new THREE.Vector2();
  activeTool: EditorTool = $activeTool.get();
  unsubscribeToolStore: (() => void) | null = null;
  unsubscribeSettingsStore: (() => void) | null = null;
  dragPointerId: number | null = null;
  activePointerHandler: ScenePointerInputHandler | null = null;
  lastFrameTime = performance.now();
  rafId = 0;
  disposed = false;

  constructor(readonly container: HTMLElement) {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(options.background);
    this.scene.fog = new THREE.Fog(
      options.background,
      options.fog.near,
      options.fog.far,
    );

    this.camera = new THREE.PerspectiveCamera(
      options.camera.fov,
      options.camera.aspect,
      options.camera.near,
      options.camera.far,
    );
    this.cameraController = new SceneCameraController(this.camera, {
      distance: options.camera.distance,
      angleDegrees: $cameraAngleToOrigin.get(),
    });

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(container.clientWidth, container.clientHeight, false);
    container.appendChild(this.renderer.domElement);

    this.activeSelection = new ActiveSelection();
    this.selectionInputHandler = new SceneSelectionInputHandler(
      this.activeSelection,
      this.selectMarqueeTool,
    );
    this.pointerInputHandlers.push(
      this.placeInputHandler,
      this.selectionInputHandler,
    );

    this.sceneObjects = new SceneObjectCollection([
      new Floor({
        size: options.floorSize,
        textureRepeat: options.floorTextureRepeat,
      }),
      this.activeSelection,
    ]);

    this.sceneObjects.renderAll(this.scene);
    this.placeTool.render(this.scene);

    this.scene.add(new THREE.AmbientLight(0xffffff, 1.2));

    const sunLight = new THREE.DirectionalLight(0xfff3d6, 2.5);
    sunLight.position.set(8, 14, 6);
    this.scene.add(sunLight);

    this.observer = new ResizeObserver(this.handleResize);
    this.observer.observe(container);

    this.activeSelection.setActiveTool(this.activeTool);
    this.unsubscribeToolStore = $activeTool.subscribe(tool => {
      this.handleToolChange(tool);
    });
    this.unsubscribeSettingsStore = $cameraAngleToOrigin.subscribe(angle => {
      this.setCameraAngleDegrees(angle);
    });
    this.keybindHandler.attach();
    for (const pointerInputHandler of this.pointerInputHandlers) {
      pointerInputHandler.attach?.();
    }

    this.renderer.domElement.addEventListener(
      'pointerdown',
      this.handlePointerDown,
    );

    this.handleResize();
    this.renderFrame();
  }

  dispose() {
    if (this.disposed) {
      return;
    }

    this.disposed = true;
    cancelAnimationFrame(this.rafId);
    this.observer.disconnect();
    if (this.unsubscribeToolStore) {
      this.unsubscribeToolStore();
      this.unsubscribeToolStore = null;
    }
    if (this.unsubscribeSettingsStore) {
      this.unsubscribeSettingsStore();
      this.unsubscribeSettingsStore = null;
    }
    this.keybindHandler.detach();
    for (const pointerInputHandler of this.pointerInputHandlers) {
      pointerInputHandler.detach?.();
    }
    this.renderer.domElement.removeEventListener(
      'pointerdown',
      this.handlePointerDown,
    );
    this.releasePointerCapture();

    this.sceneObjects.disposeAll();
    this.placeTool.dispose();

    if (this.renderer.domElement.parentElement === this.container) {
      this.container.removeChild(this.renderer.domElement);
    }
    this.renderer.dispose();
  }

  setCameraAngleDegrees(angleDegrees: number) {
    this.cameraController.setAngleDegrees(angleDegrees);
  }

  getCameraAngleDegrees() {
    return this.cameraController.getAngleDegrees();
  }

  readonly handleResize = () => {
    if (this.disposed) {
      return;
    }

    const { clientWidth, clientHeight } = this.container;
    if (clientWidth === 0 || clientHeight === 0) {
      return;
    }

    this.cameraController.setViewportSize(clientWidth, clientHeight);
    this.renderer.setSize(clientWidth, clientHeight, false);
  };

  readonly renderFrame = () => {
    if (this.disposed) {
      return;
    }

    const now = performance.now();
    const deltaSeconds = (now - this.lastFrameTime) / 1000;
    this.lastFrameTime = now;

    this.sceneObjects.updateAll(deltaSeconds);

    this.renderer.render(this.scene, this.camera);
    this.rafId = window.requestAnimationFrame(this.renderFrame);
  };

  readonly handlePointerDown = (event: PointerEvent) => {
    if (this.disposed) {
      return;
    }

    this.updateRayFromPointer(event);

    const pointerContext = this.createPointerContext(event);
    for (const pointerInputHandler of this.pointerInputHandlers) {
      const result = pointerInputHandler.onPointerDown(pointerContext);
      if (result === 'ignored') {
        continue;
      }

      if (result === 'captured') {
        this.capturePointer(event.pointerId, pointerInputHandler);
      }

      return;
    }
  };

  readonly handlePointerMove = (event: PointerEvent) => {
    if (this.disposed) {
      return;
    }

    if (!this.activePointerHandler) {
      return;
    }

    if (this.dragPointerId !== null && this.dragPointerId !== event.pointerId) {
      return;
    }

    this.updateRayFromPointer(event);
    this.activePointerHandler.onPointerMove(this.createPointerContext(event));
  };

  readonly handlePointerUp = (event: PointerEvent) => {
    if (this.disposed) {
      return;
    }

    if (!this.activePointerHandler) {
      return;
    }

    if (this.dragPointerId !== null && this.dragPointerId !== event.pointerId) {
      return;
    }

    this.updateRayFromPointer(event);
    this.activePointerHandler.onPointerUp(this.createPointerContext(event));
    this.releasePointerCapture();
  };

  handleToolChange(tool: EditorTool) {
    this.activeTool = tool;
    this.activeSelection.setActiveTool(tool);

    if (this.activePointerHandler) {
      this.activePointerHandler.cancel();
      this.releasePointerCapture();
    }
  }

  canSelectObjects() {
    return getActiveToolConfig()?.canSelectObjects ?? true;
  }

  placeSceneObject(sceneObject: SelectableObject) {
    sceneObject.render(this.scene);
    this.sceneObjects.add(sceneObject);
    deselectAll();
    sceneObject.select();
  }

  addSelectableObject(sceneObject: SelectableObject<any>) {
    sceneObject.render(this.scene);
    this.sceneObjects.add(sceneObject);
  }

  capturePointer(
    pointerId: number,
    pointerInputHandler: ScenePointerInputHandler,
  ) {
    this.activePointerHandler = pointerInputHandler;
    this.dragPointerId = pointerId;
    this.renderer.domElement.setPointerCapture(pointerId);
    window.addEventListener('pointermove', this.handlePointerMove);
    window.addEventListener('pointerup', this.handlePointerUp);
  }

  releasePointerCapture() {
    window.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('pointerup', this.handlePointerUp);

    if (this.dragPointerId !== null) {
      this.renderer.domElement.releasePointerCapture(this.dragPointerId);
    }
    this.dragPointerId = null;
    this.activePointerHandler = null;
  }

  deleteSelectedObjects() {
    const selectedObjects = [...$selectedObjects.get()];
    if (selectedObjects.length === 0) {
      return;
    }

    if (this.activePointerHandler) {
      this.activePointerHandler.cancel();
      this.releasePointerCapture();
    }

    for (const selectedObject of selectedObjects) {
      this.sceneObjects.remove(selectedObject);

      selectedObject.dispose();
    }
  }

  clearSceneObjects() {
    this.sceneObjects.clear();
    deselectAll();
  }

  createPointerContext(event: PointerEvent): ScenePointerContext {
    return {
      event,
      raycaster: this.raycaster,
      camera: this.camera,
      sceneObjects: this.sceneObjects.getAll(),
      activeSelection: this.activeSelection,
      activeTool: this.activeTool,
      canSelectObjects: this.canSelectObjects(),
      onSelectMarqueeRect: selectionRect => {
        this.marqueeViewportSelector.selectContainedObjects(
          this.sceneObjects.getAll(),
          this.camera,
          this.renderer.domElement.getBoundingClientRect(),
          selectionRect,
        );
      },
    };
  }

  updateRayFromPointer(event: PointerEvent) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -(((event.clientY - rect.top) / rect.height) * 2 - 1);
    this.raycaster.setFromCamera(this.pointer, this.camera);
  }
}
